import { ChangeDetectionStrategy, Component, computed, effect, input, linkedSignal, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { EmailMessage, EmailMessageConversation, EmailMessageService } from "../../../services/email.message.service";
import { ContactService } from "../../../services/contact.service";
import { EmailService } from "../../../services/email.service";
import { FlsService } from "../../../services/fls.service";
import { NotificationService } from "../../../services/notification.service";
import { S25PipesModule } from "../../../pipes/s25.pipes.module";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { SeEmailSentFilterComponent } from "./se.email.sent.filter.component";
import { SeEmailSentDetailComponent } from "./se.email.sent.detail.component";
import {
    appendConvoFooter,
    buildEmailQuery,
    buildFixedEventFilter,
    buildPaginationRangeLabel,
    buildReplySubject,
    buildSentListRow,
    DEFAULT_EMAIL_SENT_FILTER,
    EMAIL_SENT_DATE_FORMAT,
    EMAIL_SENT_DATE_PART_FORMAT,
    EMAIL_SENT_TIME_PART_FORMAT,
    EmailSentFilter,
    EmailSentListRow,
    formatThreadCountLabel,
    formatUnreadCountLabel,
    formatTotalCountLabel,
    getConversationVisibleMessageCount,
    getFallbackExpandedMessageIds,
    getMessageSubject,
    getRelayMailboxRecipient,
    getReplyContextIds,
    getReplyMailboxPreview,
    getUnreadExpandedMessageIds,
    getThreadMessageFromLabel,
    getUnreadRecipientIdsForContact,
    partitionThreadMessages,
    stripHtmlToPlainText,
} from "./email.sent.util";

const COMPACT_LAYOUT_MAX_WIDTH_PX = 1100;

const DEFAULT_SENT_SCOPE_HINT = "Emails sent from Connect25.";

interface EmailReplyDraft {
    composerOpen: boolean;
    body: string;
}

const EMPTY_REPLY_DRAFT: EmailReplyDraft = { composerOpen: false, body: "" };

function isEmptyReplyDraft(draft: EmailReplyDraft): boolean {
    return !draft.composerOpen && draft.body === "";
}

/**
 * Connect25 Sent mail: paginated conversation list plus thread detail with in-app reply.
 *
 * List rows are conversations. Each row shows the latest message's subject, sender, unread count,
 * and a short plain-text preview (thread preview) so users can scan without opening the thread.
 *
 * Selecting a row loads the full thread in the detail pane. Inbound replies often include
 * "quoted text" — the previous email repeated in HTML below Outlook markers (e.g. divRplyFwdMsg).
 * That quoted block is hidden by default; the user can expand it per message via "Show quoted text".
 *
 * Expanding a thread message marks unread recipient rows read for the current contact (batched PUT).
 * Unread chips on the list may update optimistically until the next search refresh.
 */
@TypeManagerDecorator("s25-ng-se-email-sent")
@Component({
    selector: "s25-ng-se-email-sent",
    host: { class: "email-sent" },
    template: `
        <p class="sent-hint" role="note">{{ scopeHint() }}</p>

        <s25-ng-se-email-sent-filter
            [(filter)]="filterDraft"
            [showEventsFilter]="showEventsFilter()"
            [showOrganizationsFilter]="showOrganizationsFilter()"
            [clearFilterBaseline]="filterBaseline()"
            (searchSubmit)="onSearch()"
            (clearFilters)="onClearFilters()"
        />

        <div
            class="sent-layout"
            [class.sent-layout--fullscreen]="detailExpanded()"
            [class.sent-layout--mobile-detail-open]="showDetailView()"
            [class.sent-layout--split]="sidePanelOpen()"
        >
            <div class="message-table-wrapper">
                <section class="message-table" aria-label="Message list">
                    @if (sentResource.isLoading() && !sentConversations().length) {
                        <div class="sent-status" role="status">
                            <s25-ng-loading-inline-static />
                            <span>Loading messages...</span>
                        </div>
                    } @else if (!hasSearched()) {
                        <div class="sent-empty" role="status">
                            <p>Use Search to load your messages.</p>
                        </div>
                    } @else if (!sentConversations().length) {
                        <div class="sent-empty" role="status">
                            <p>No messages match your filters.</p>
                        </div>
                    } @else {
                        <ul class="message-list" role="list">
                            @for (row of sentListRows(); track row.conversation.convo_uuid) {
                                @if (row.displayMessage; as message) {
                                    <li class="message-list__item">
                                        <button
                                            type="button"
                                            class="message-row"
                                            [class.message-row--selected]="
                                                selectedConvoUuid() === row.conversation.convo_uuid
                                            "
                                            [class.message-row--unread]="!!rowUnreadLabel(row.conversation)"
                                            (click)="onSelectConversation(row)"
                                        >
                                            <span class="message-row__primary">
                                                <span class="message-row__subject-line">
                                                    <span class="message-row__subject">{{ rowSubject(message) }}</span>
                                                    @if (
                                                        rowUnreadLabel(row.conversation) ||
                                                        rowTotalLabel(row.conversation)
                                                    ) {
                                                        <span class="message-row__chips">
                                                            @if (rowUnreadLabel(row.conversation); as unreadLabel) {
                                                                <span
                                                                    class="message-row__unread-chip"
                                                                    data-id="unread-chip"
                                                                    >{{ unreadLabel }}</span
                                                                >
                                                            }
                                                            @if (rowTotalLabel(row.conversation); as totalLabel) {
                                                                <span
                                                                    class="message-row__total-chip"
                                                                    data-id="total-chip"
                                                                    >{{ totalLabel }}</span
                                                                >
                                                            }
                                                        </span>
                                                    }
                                                </span>
                                                @if (message.received_at) {
                                                    <span class="message-row__datetime">
                                                        <time
                                                            class="message-row__date"
                                                            [attr.datetime]="message.received_at"
                                                        >
                                                            {{ message.received_at | date: sentDatePartFormat }}
                                                        </time>
                                                        <time
                                                            class="message-row__time"
                                                            [attr.datetime]="message.received_at"
                                                        >
                                                            {{ message.received_at | date: sentTimePartFormat }}
                                                        </time>
                                                    </span>
                                                }
                                            </span>
                                            <span class="message-row__secondary">
                                                <span class="message-row__from">{{ rowFromLabel(message) }}</span>
                                                @if (row.preview) {
                                                    <span class="message-row__preview">{{ row.preview }}</span>
                                                }
                                            </span>
                                        </button>
                                    </li>
                                }
                            }
                        </ul>

                        @if (hasSearched() && (canGoPrev() || canGoNext() || totalResults() > 0)) {
                            <footer class="message-table__footer">
                                <span class="pagination-summary">{{ paginationSummary() }}</span>
                                @if (canGoPrev() || canGoNext()) {
                                    <div class="pagination-controls">
                                        <button
                                            type="button"
                                            class="aw-button aw-button--outline"
                                            [disabled]="!canGoPrev() || sentResource.isLoading()"
                                            (click)="goPrevPage()"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            type="button"
                                            class="aw-button aw-button--outline"
                                            [disabled]="!canGoNext() || sentResource.isLoading()"
                                            (click)="goNextPage()"
                                        >
                                            Next
                                        </button>
                                    </div>
                                }
                            </footer>
                        }
                    }
                </section>
            </div>

            @if (sidePanelOpen()) {
                <section class="sent-detail-pane sent-detail-pane--side" aria-label="Conversation detail">
                    <s25-ng-se-email-sent-detail
                        [convoUuid]="selectedConvoUuid()"
                        [isLoading]="threadResource.isLoading()"
                        [messages]="threadMessages()"
                        [headerMessage]="detailHeaderMessage()"
                        [replyMessages]="threadReplyMessages()"
                        [originalMessage]="threadOriginalMessage()"
                        [isMultiMessageThread]="isMultiMessageThread()"
                        [threadCountLabel]="threadCountLabel()"
                        [expandedMessageIds]="expandedMessageIds()"
                        [showQuotedByMessageId]="showQuotedByMessageId()"
                        [showSideToolbar]="sidePanelOpen()"
                        [datePartFormat]="sentDatePartFormat"
                        [timePartFormat]="sentTimePartFormat"
                        [canReply]="canReply()"
                        [replyHint]="replyHint()"
                        [replySending]="replySending()"
                        [replyToLabels]="replyToLabels()"
                        [replySubject]="replySubject()"
                        [replyComposerOpen]="replyDraftComposerOpen()"
                        (replyComposerOpenChange)="onReplyDraftComposerOpenChange($event)"
                        [replyBody]="replyDraftBody()"
                        (replyBodyChange)="onReplyDraftBodyChange($event)"
                        (expandFullscreen)="expandDetailView()"
                        (closeDetail)="closeDetailCompletely()"
                        (toggleExpanded)="toggleMessageExpanded($event)"
                        (showQuoted)="showQuoted($event)"
                        (replySend)="onReplySend($event)"
                    />
                </section>
            }

            @if (fullscreenDetailOpen()) {
                <section class="detail-view detail-view--fullscreen" aria-label="Conversation detail full screen">
                    <header class="detail-view__toolbar">
                        @if (detailExpanded()) {
                            <button type="button" class="detail-back-btn" (click)="collapseDetailView()">
                                <s25-ng-icon [type]="'collapse'" [size]="16" [ariaLabel]="false" />
                                Exit full screen
                            </button>
                        } @else {
                            <button type="button" class="detail-back-btn" (click)="closeDetailView()">
                                <s25-ng-icon [type]="'arrowLeft'" [size]="16" [ariaLabel]="false" />
                                Back to list
                            </button>
                        }
                    </header>
                    <s25-ng-se-email-sent-detail
                        [convoUuid]="selectedConvoUuid()"
                        [isLoading]="threadResource.isLoading()"
                        [messages]="threadMessages()"
                        [headerMessage]="detailHeaderMessage()"
                        [replyMessages]="threadReplyMessages()"
                        [originalMessage]="threadOriginalMessage()"
                        [isMultiMessageThread]="isMultiMessageThread()"
                        [threadCountLabel]="threadCountLabel()"
                        [expandedMessageIds]="expandedMessageIds()"
                        [showQuotedByMessageId]="showQuotedByMessageId()"
                        [showSideToolbar]="false"
                        [datePartFormat]="sentDatePartFormat"
                        [timePartFormat]="sentTimePartFormat"
                        [canReply]="canReply()"
                        [replyHint]="replyHint()"
                        [replySending]="replySending()"
                        [replyToLabels]="replyToLabels()"
                        [replySubject]="replySubject()"
                        [replyComposerOpen]="replyDraftComposerOpen()"
                        (replyComposerOpenChange)="onReplyDraftComposerOpenChange($event)"
                        [replyBody]="replyDraftBody()"
                        (replyBodyChange)="onReplyDraftBodyChange($event)"
                        (expandFullscreen)="expandDetailView()"
                        (closeDetail)="closeDetailCompletely()"
                        (toggleExpanded)="toggleMessageExpanded($event)"
                        (showQuoted)="showQuoted($event)"
                        (replySend)="onReplySend($event)"
                    />
                </section>
            }
        </div>
    `,
    styles: `
        :host {
            display: block;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
        }

        .sent-hint {
            margin: 0 0 1rem;
            font-size: 0.8125rem;
            color: var(--color--text-muted);
        }

        .sent-layout {
            display: flex;
            gap: 1.25rem;
            align-items: flex-start;
            min-height: 24rem;
        }

        .message-table-wrapper {
            flex: 1;
            min-width: 0;
            container-type: inline-size;
            container-name: message-list;
        }

        .message-table {
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border: 1px solid color-mix(in srgb, var(--color--border-disabled) 70%, transparent);
            border-radius: 12px;
            background: var(--color--surface-bg);
            box-shadow: 0 1px 3px color-mix(in srgb, var(--color--text) 4%, transparent);
            min-height: 20rem;
        }

        .message-list {
            list-style: none;
            margin: 0;
            padding: 0.5rem;
            overflow-y: auto;
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 0.375rem;
        }

        .message-list__item {
            margin: 0;
        }

        .message-row {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            width: 100%;
            text-align: left;
            padding: 0.75rem 0.875rem;
            border: 1px solid transparent;
            border-radius: 8px;
            background: color-mix(in srgb, var(--color--page-bg) 40%, var(--color--surface-bg));
            box-shadow: none;
            cursor: pointer;
            transition:
                border-color var(--transition-medium) ease,
                background var(--transition-medium) ease;
        }

        .message-row:hover {
            border-color: color-mix(in srgb, var(--color--border-disabled) 80%, transparent);
            background: var(--color--surface-bg-hover);
            box-shadow: none;
        }

        .message-row--selected {
            border-color: rgba(var(--color-primary-rgba), 0.35);
            background: rgba(var(--color-primary-rgba), 0.06);
            box-shadow: none;
        }

        .message-row--selected:hover {
            background: rgba(var(--color-primary-rgba), 0.08);
        }

        .message-row__primary {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 1rem;
            min-width: 0;
        }

        .message-row__secondary {
            display: flex;
            align-items: baseline;
            gap: 0.625rem;
            min-width: 0;
            font-size: 0.75rem;
        }

        .message-row__subject-line {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.375rem 0.5rem;
            min-width: 0;
            flex: 1;
        }

        .message-row__unread-chip,
        .message-row__total-chip {
            display: inline-flex;
            align-items: center;
            padding: 0.1875rem 0.5rem;
            border-radius: 999px;
            font-size: 0.6875rem;
            font-weight: 600;
            line-height: 1.25;
            white-space: nowrap;
        }

        .message-row__unread-chip {
            flex-shrink: 0;
            background: rgba(var(--color-primary-rgba), 0.12);
            color: var(--color--text-strong);
        }

        .message-row__chips {
            display: inline-flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.25rem;
            flex-shrink: 0;
            max-width: 100%;
        }

        .message-row__total-chip {
            background: color-mix(in srgb, var(--color--text-muted) 12%, transparent);
            color: var(--color--text-muted);
        }

        .message-row--unread .message-row__subject {
            font-weight: 700;
        }

        .message-row__from {
            flex-shrink: 0;
            max-width: 45%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-weight: 500;
            color: var(--color--text);
        }

        .message-row__subject {
            display: block;
            flex: 1 1 8rem;
            min-width: 0;
            font-size: 0.875rem;
            font-weight: 600;
            color: var(--color--text-strong);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .message-row__preview {
            flex: 1;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--color--text-muted);
        }

        .message-row__datetime {
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 0.125rem;
            text-align: right;
        }

        .message-row__date {
            font-size: 0.6875rem;
            font-weight: 500;
            letter-spacing: 0.02em;
            color: var(--color--text-muted);
            white-space: nowrap;
        }

        .message-row__time {
            font-size: 0.625rem;
            color: var(--color--text-muted);
            white-space: nowrap;
        }

        .message-table__footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 0.75rem;
            padding: 0.625rem 0.875rem 0.75rem;
            border-top: 1px solid color-mix(in srgb, var(--color--border-disabled) 60%, transparent);
            background: color-mix(in srgb, var(--color--page-bg) 35%, var(--color--surface-bg));
            flex-wrap: wrap;
        }

        .pagination-summary {
            font-size: 0.875rem;
            color: var(--color--text-muted);
        }

        .pagination-controls {
            display: flex;
            gap: 0.5rem;
        }

        .sent-empty,
        .sent-status {
            padding: 2rem 1rem;
            text-align: center;
            color: var(--color--text-muted);
        }

        .sent-status {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.75rem;
        }

        .sent-detail-pane--side {
            flex-shrink: 0;
            width: 560px;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            overflow-y: auto;
            border: 1px solid color-mix(in srgb, var(--color--border-disabled) 70%, transparent);
            border-radius: 12px;
            background: var(--color--surface-bg);
            box-shadow: 0 1px 3px color-mix(in srgb, var(--color--text) 4%, transparent);
            min-height: 20rem;
        }

        .detail-view--fullscreen {
            width: 100%;
            display: flex;
            flex-direction: column;
            border: 1px solid color-mix(in srgb, var(--color--border-disabled) 70%, transparent);
            border-radius: 12px;
            background: var(--color--surface-bg);
            box-shadow: 0 1px 3px color-mix(in srgb, var(--color--text) 4%, transparent);
            min-height: 20rem;
            overflow-y: auto;
        }

        .sent-layout--fullscreen .message-table-wrapper {
            display: none;
        }

        .sent-layout--fullscreen .sent-detail-pane--side {
            display: none;
        }

        .detail-view__toolbar {
            display: flex;
            align-items: center;
            padding: 0.5rem 1.25rem;
            border-bottom: none;
            background: transparent;
        }

        .detail-back-btn {
            display: inline-flex;
            align-items: center;
            gap: 0.375rem;
            padding: 0.375rem 0;
            border: none;
            background: none;
            color: var(--color--link-text);
            cursor: pointer;
            font-size: 0.875rem;
        }

        .sent-layout--split .message-table-wrapper {
            flex: 1 1 0;
            min-width: 11rem;
        }

        .sent-layout--split .message-row__secondary {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25rem;
            width: 100%;
        }

        .sent-layout--split .message-row__from {
            max-width: 100%;
        }

        .sent-layout--split .message-row__preview {
            width: 100%;
            white-space: normal;
            overflow-wrap: break-word;
            word-break: break-word;
            text-overflow: unset;
        }

        @container message-list (max-width: 36rem) {
            .message-row__primary {
                flex-direction: column;
                align-items: stretch;
                gap: 0.5rem;
            }

            .message-row__subject-line {
                width: 100%;
            }

            .message-row__subject {
                flex-basis: 100%;
            }

            .message-row__chips {
                flex-basis: 100%;
            }

            .message-row__datetime {
                flex-direction: column;
                align-items: flex-start;
                width: 100%;
                text-align: left;
            }

            .message-row__secondary {
                flex-direction: column;
                align-items: flex-start;
                gap: 0.25rem;
                width: 100%;
            }

            .message-row__from {
                max-width: 100%;
            }

            .message-row__preview {
                width: 100%;
                white-space: normal;
                overflow-wrap: break-word;
                word-break: break-word;
                text-overflow: unset;
            }
        }

        @media (max-width: 1100px) {
            .sent-layout {
                flex-direction: column;
                gap: 0;
            }

            .message-table-wrapper {
                width: 100%;
            }

            .message-table {
                width: 100%;
                border-radius: 12px;
            }

            .sent-layout--mobile-detail-open .message-table-wrapper {
                display: none;
            }

            .sent-detail-pane--side {
                display: none;
            }

            .message-table__footer {
                padding: 0.75rem 1rem 1rem;
            }
        }

        @media (max-width: 768px) {
            .message-row {
                padding: 0.875rem 1rem;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        S25PipesModule,
        S25LoadingInlineModule,
        S25IconComponent,
        SeEmailSentFilterComponent,
        SeEmailSentDetailComponent,
    ],
})
export class SeEmailSentComponent {
    readonly scopeHint = input(DEFAULT_SENT_SCOPE_HINT);
    readonly fixedEventId = input<number | undefined>(undefined);

    readonly sentDateFormat = EMAIL_SENT_DATE_FORMAT;
    readonly sentDatePartFormat = EMAIL_SENT_DATE_PART_FORMAT;
    readonly sentTimePartFormat = EMAIL_SENT_TIME_PART_FORMAT;

    readonly filterBaseline = computed((): EmailSentFilter => {
        const eventId = this.fixedEventId();
        if (eventId != null && Number.isFinite(eventId)) {
            return buildFixedEventFilter(eventId);
        }
        return { ...DEFAULT_EMAIL_SENT_FILTER };
    });

    readonly isEventScopedView = computed((): boolean => {
        const eventId = this.fixedEventId();
        return eventId != null && Number.isFinite(eventId);
    });

    readonly showEventsFilter = computed(() => !this.isEventScopedView());
    readonly showOrganizationsFilter = computed(() => !this.isEventScopedView());

    readonly filterDraft = linkedSignal(() => ({ ...this.filterBaseline() }));
    readonly appliedFilters = linkedSignal(() => ({ ...this.filterBaseline() }));
    readonly hasSearched = signal(true);
    readonly pageNum = signal(1);
    /** Server pagination cursor; omitted on page 1, set from each response for page 2+. */
    readonly paginateKey = signal<string | undefined>(undefined);
    /** Consumed once in sentResource loader so search/clear can pass clearCache without retriggering params. */
    private clearCacheOnNextLoad = true;
    readonly selectedConvoUuid = signal<string | undefined>(undefined);
    readonly showDetailView = signal(false);
    readonly detailExpanded = signal(false);
    readonly expandedMessageIds = signal<Set<number>>(new Set());
    readonly showQuotedByMessageId = signal<Set<number>>(new Set());
    /** Recipient ids treated as read locally after a successful mark-read PUT. */
    readonly locallyReadRecipientIds = signal<Set<number>>(new Set());
    /** Recipient ids currently in flight on mark-read PUT; avoids duplicate requests. */
    readonly markingReadRecipientIds = signal<Set<number>>(new Set());
    /** Optimistic unread count per convo_uuid on the list row after mark-read (until next search). */
    readonly localUnreadCountOverrides = signal<Map<string, number>>(new Map());
    readonly replySending = signal(false);
    private readonly replyDraftByConvoUuid = signal<Map<string, EmailReplyDraft>>(new Map());

    readonly flsResource = s25Resource({ loader: () => FlsService.getFls() });

    readonly canReply = computed(() => this.flsResource.value()?.EMAIL_OPTIONS === "F");

    readonly replyHint = computed(() =>
        this.canReply() ? "Reply is sent to the conversation mailbox and forwarded per server routing rules." : "",
    );

    readonly contactIdResource = s25Resource({
        loader: () => ContactService.getCurrentId(),
    });

    /** Current user's contact id; negative ids are valid — only undefined/null are excluded. */
    readonly currentContactId = computed((): number | undefined => {
        const contactId = this.contactIdResource.value();
        return S25Util.isUndefined(contactId) ? undefined : contactId;
    });

    readonly sentResource = s25Resource({
        if: () => !!this.currentContactId() && this.hasSearched(),
        params: () => ({
            filter: this.appliedFilters(),
            page: this.pageNum(),
            paginateKey: this.pageNum() > 1 ? this.paginateKey() : undefined,
            contactId: this.currentContactId()!,
        }),
        loader: async ({ params }) => {
            const clearCache = this.clearCacheOnNextLoad;
            this.clearCacheOnNextLoad = false;

            const response = await EmailMessageService.queryMessages(
                buildEmailQuery(params.filter, params.contactId),
                params.page,
                params.paginateKey,
                clearCache,
            );

            if (response.paginate_key) {
                this.paginateKey.set(response.paginate_key);
            }

            return response;
        },
    });

    readonly threadResource = s25Resource({
        if: () => !!this.selectedConvoUuid(),
        params: () => ({
            convoUuid: this.selectedConvoUuid()!,
            contactId: this.currentContactId(),
        }),
        loader: ({ params }) => EmailMessageService.getThread(params.convoUuid, params.contactId),
    });

    readonly sentConversations = computed(() => this.sentResource.value()?.conversation ?? []);

    readonly sentListRows = computed(() => this.sentConversations().map(buildSentListRow));

    readonly threadConversation = computed(() => {
        const conversation = this.threadResource.value();
        const selectedConvoUuid = this.selectedConvoUuid();
        if (!conversation || !selectedConvoUuid || conversation.convo_uuid !== selectedConvoUuid) {
            return undefined;
        }

        return conversation;
    });

    readonly threadMessages = computed(() => this.threadConversation()?.email_message ?? []);

    readonly threadPartition = computed(() => partitionThreadMessages(this.threadMessages()));

    readonly threadOriginalMessage = computed(() => this.threadPartition().original);

    readonly threadReplyMessages = computed(() => this.threadPartition().replies);

    readonly threadMessageCount = computed(() => {
        const conversation = this.threadConversation();
        if (conversation) {
            return getConversationVisibleMessageCount(conversation);
        }
        return this.threadMessages().length;
    });

    readonly isMultiMessageThread = computed(() => this.threadMessageCount() > 1);

    readonly threadCountLabel = computed(() => formatThreadCountLabel(this.threadMessageCount()));

    readonly detailHeaderMessage = computed((): EmailMessage | undefined => {
        const originalMessage = this.threadOriginalMessage();
        if (originalMessage) {
            return originalMessage;
        }

        const convoUuid = this.selectedConvoUuid();
        if (!convoUuid) {
            return undefined;
        }

        const listRow = this.sentListRows().find((row) => row.conversation.convo_uuid === convoUuid);
        return listRow?.displayMessage;
    });

    readonly replyToLabels = computed(() => getReplyMailboxPreview());

    readonly replySubject = computed(() => buildReplySubject(this.detailHeaderMessage()));

    readonly replyDraftComposerOpen = computed(() => this.getReplyDraft(this.selectedConvoUuid()).composerOpen);

    readonly replyDraftBody = computed(() => this.getReplyDraft(this.selectedConvoUuid()).body);

    readonly sidePanelOpen = computed(
        () => !!this.selectedConvoUuid() && !this.detailExpanded() && !this.showDetailView(),
    );

    readonly fullscreenDetailOpen = computed(() => this.detailExpanded() || this.showDetailView());

    readonly pageCount = computed(() => Number(this.sentResource.value()?.page_count ?? 0));

    readonly totalResults = computed(() => Number(this.sentResource.value()?.total_results ?? 0));

    readonly canGoPrev = computed(() => this.pageNum() > 1);

    readonly canGoNext = computed(() => {
        const pageCount = this.pageCount();
        return pageCount > 0 && this.pageNum() < pageCount;
    });

    readonly paginationSummary = computed(() =>
        buildPaginationRangeLabel(
            this.pageNum(),
            this.pageCount(),
            this.totalResults(),
            this.sentConversations().length,
        ),
    );

    constructor() {
        effect(() => {
            const loadError = this.sentResource.error();
            if (loadError) {
                S25Util.showError(loadError);
            }
        });

        effect(() => {
            const loadError = this.threadResource.error();
            if (loadError) {
                S25Util.showError(loadError);
            }
        });

        // When a thread loads (list preview → full thread), auto-expand unread inbound messages for
        // the current contact, or the latest message only while contactIdResource is still loading.
        // Thread fetch is not gated on currentContactId(), so skip mark-read until we have a contact.
        effect(() => {
            const contactId = this.currentContactId();
            const messages = this.threadMessages();

            if (!messages.length) {
                this.expandedMessageIds.set(new Set());
                this.showQuotedByMessageId.set(new Set());
                return;
            }

            const expandedIds = S25Util.isDefined(contactId)
                ? getUnreadExpandedMessageIds(messages, contactId)
                : getFallbackExpandedMessageIds(messages);

            this.expandedMessageIds.set(expandedIds);
            this.showQuotedByMessageId.set(new Set());

            if (S25Util.isDefined(contactId)) {
                this.markExpandedMessagesReadIfNeeded(messages, expandedIds);
            }
        });
    }

    /** Clears optimistic read/unread state when filters change or detail is closed. */
    private resetReadTrackingState(): void {
        this.locallyReadRecipientIds.set(new Set());
        this.markingReadRecipientIds.set(new Set());
        this.localUnreadCountOverrides.set(new Map());
    }

    onSearch(): void {
        this.appliedFilters.set({ ...this.filterDraft() });
        this.pageNum.set(1);
        this.paginateKey.set(undefined);
        this.clearCacheOnNextLoad = true;
        this.hasSearched.set(true);
        this.resetReadTrackingState();
        this.closeDetailCompletely();
    }

    onClearFilters(): void {
        const cleared = { ...this.filterBaseline() };
        this.appliedFilters.set(cleared);
        this.filterDraft.set(cleared);
        this.pageNum.set(1);
        this.paginateKey.set(undefined);
        this.clearCacheOnNextLoad = true;
        this.resetReadTrackingState();
        this.closeDetailCompletely();
    }

    goPrevPage(): void {
        if (!this.canGoPrev()) {
            return;
        }
        this.pageNum.update((page) => page - 1);
    }

    goNextPage(): void {
        if (!this.canGoNext()) {
            return;
        }
        this.pageNum.update((page) => page + 1);
    }

    onSelectConversation(row: EmailSentListRow): void {
        const convoUuid = row.conversation.convo_uuid;
        if (!convoUuid) {
            return;
        }

        this.selectedConvoUuid.set(convoUuid);
        this.detailExpanded.set(false);
        this.showDetailView.set(this.isMobileLayout());
    }

    expandDetailView(): void {
        this.detailExpanded.set(true);
    }

    collapseDetailView(): void {
        this.detailExpanded.set(false);
    }

    closeDetailView(): void {
        this.showDetailView.set(false);
    }

    closeDetailCompletely(): void {
        this.clearReplyDraft(this.selectedConvoUuid());
        this.selectedConvoUuid.set(undefined);
        this.showDetailView.set(false);
        this.detailExpanded.set(false);
    }

    onReplyDraftComposerOpenChange(composerOpen: boolean): void {
        this.patchReplyDraft(this.selectedConvoUuid(), { composerOpen });
    }

    onReplyDraftBodyChange(body: string): void {
        this.patchReplyDraft(this.selectedConvoUuid(), { body });
    }

    private getReplyDraft(convoUuid: string | undefined): EmailReplyDraft {
        if (!convoUuid) {
            return EMPTY_REPLY_DRAFT;
        }

        return this.replyDraftByConvoUuid().get(convoUuid) ?? EMPTY_REPLY_DRAFT;
    }

    private patchReplyDraft(convoUuid: string | undefined, patch: Partial<EmailReplyDraft>): void {
        if (!convoUuid) {
            return;
        }

        this.replyDraftByConvoUuid.update((drafts) => {
            const nextDrafts = new Map(drafts);
            const currentDraft = nextDrafts.get(convoUuid) ?? EMPTY_REPLY_DRAFT;
            const mergedDraft: EmailReplyDraft = { ...currentDraft, ...patch };
            if (isEmptyReplyDraft(mergedDraft)) {
                nextDrafts.delete(convoUuid);
            } else {
                nextDrafts.set(convoUuid, mergedDraft);
            }
            return nextDrafts;
        });
    }

    private clearReplyDraft(convoUuid: string | undefined): void {
        if (!convoUuid) {
            return;
        }

        this.replyDraftByConvoUuid.update((drafts) => {
            if (!drafts.has(convoUuid)) {
                return drafts;
            }

            const nextDrafts = new Map(drafts);
            nextDrafts.delete(convoUuid);
            return nextDrafts;
        });
    }

    rowFromLabel(message: EmailMessage): string {
        return getThreadMessageFromLabel(message);
    }

    rowSubject(message: EmailMessage): string {
        return getMessageSubject(message);
    }

    /** Unread label for a list row; uses localUnreadCountOverrides after mark-read. */
    rowUnreadLabel(conversation: EmailMessageConversation): string {
        const convoUuid = conversation.convo_uuid;
        if (!convoUuid) {
            return "";
        }

        const localOverride = this.localUnreadCountOverrides().get(convoUuid);
        const unreadCount = localOverride ?? conversation.message_unread_count ?? 0;
        return formatUnreadCountLabel(unreadCount);
    }

    rowTotalLabel(conversation: EmailMessageConversation): string {
        return formatTotalCountLabel(getConversationVisibleMessageCount(conversation));
    }

    /** Expand or collapse a thread message; first expand triggers mark-read. Collapse does not un-read. */
    toggleMessageExpanded(message: EmailMessage): void {
        const messageId = message.id;
        if (messageId == null) {
            return;
        }

        const wasExpanded = this.expandedMessageIds().has(messageId);

        this.expandedMessageIds.update((expandedIds) => {
            const nextExpandedIds = new Set(expandedIds);
            if (nextExpandedIds.has(messageId)) {
                nextExpandedIds.delete(messageId);
            } else {
                nextExpandedIds.add(messageId);
            }
            return nextExpandedIds;
        });

        if (!wasExpanded) {
            this.markExpandedMessagesReadIfNeeded(this.threadMessages(), this.expandedMessageIds());
        }
    }

    /**
     * User chose "Show quoted text" for one message. Parent tracks showQuotedByMessageId so the
     * detail pane can reveal Outlook-embedded prior-email HTML that is hidden by default.
     */
    showQuoted(message: EmailMessage): void {
        const messageId = message.id;
        if (messageId == null) {
            return;
        }

        this.showQuotedByMessageId.update((quotedIds) => {
            const nextQuotedIds = new Set(quotedIds);
            nextQuotedIds.add(messageId);
            return nextQuotedIds;
        });
    }

    onReplySend(bodyHtml: string): void {
        const convoUuid = this.selectedConvoUuid();
        const body = bodyHtml?.trim() ?? "";
        if (!convoUuid || this.replySending() || !stripHtmlToPlainText(body).length || !this.canReply()) {
            return;
        }

        let mailboxTo: string;
        try {
            mailboxTo = getRelayMailboxRecipient();
        } catch {
            NotificationService.post("Conversation mailbox is not configured.");
            return;
        }

        const subject = this.replySubject();
        const bodyWithFooter = appendConvoFooter(body, convoUuid);
        const { itemId, itemTypeId } = getReplyContextIds(this.threadMessages(), this.fixedEventId());

        this.replySending.set(true);

        const replyPromise = EmailService.sendEmail(
            subject,
            bodyWithFooter,
            mailboxTo,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            itemId,
            itemTypeId,
        )
            .then((response) => {
                if (response?.error) {
                    NotificationService.post("Reply failed: " + response.error);
                    return;
                }

                NotificationService.post("Reply submitted — it may take a moment to appear in the thread");
                this.clearReplyDraft(convoUuid);
                this.clearCacheOnNextLoad = true;
                void Promise.all([this.threadResource.reloadAndAwait(), this.sentResource.reloadAndAwait()]);
            })
            .catch((response: unknown) => {
                const errorData = (response as any)?.error?.results?.error ?? (response as any)?.error ?? response;
                NotificationService.post("Reply failed: " + S25Util.errorText(errorData, "Uncaught Error"));
            })
            .finally(() => {
                this.replySending.set(false);
            });

        NotificationService.post("Reply is being sent via conversation mailbox…", replyPromise);
    }

    /**
     * Batch PUT read for expanded unread recipient ids belonging to the current contact.
     * On success, updates locallyReadRecipientIds and may set localUnreadCountOverrides for the list row.
     */
    private markExpandedMessagesReadIfNeeded(messages: EmailMessage[], expandedIds: ReadonlySet<number>): void {
        const contactId = this.currentContactId();
        if (!contactId) {
            return;
        }

        const pendingRecipientIds: number[] = [];
        const alreadyMarking = this.markingReadRecipientIds();
        const locallyReadRecipientIds = this.locallyReadRecipientIds();

        for (const message of messages) {
            const messageId = message.id;
            if (messageId == null || !expandedIds.has(messageId)) {
                continue;
            }

            for (const recipientId of getUnreadRecipientIdsForContact(message, contactId, locallyReadRecipientIds)) {
                if (!alreadyMarking.has(recipientId) && !pendingRecipientIds.includes(recipientId)) {
                    pendingRecipientIds.push(recipientId);
                }
            }
        }

        if (!pendingRecipientIds.length) {
            return;
        }

        this.markingReadRecipientIds.update((markingIds) => {
            const nextMarkingIds = new Set(markingIds);
            pendingRecipientIds.forEach((recipientId) => nextMarkingIds.add(recipientId));
            return nextMarkingIds;
        });

        const convoUuid = this.selectedConvoUuid();

        void EmailMessageService.markRead(pendingRecipientIds, true)
            .then(() => {
                this.locallyReadRecipientIds.update((readIds) => {
                    const nextReadIds = new Set(readIds);
                    pendingRecipientIds.forEach((recipientId) => nextReadIds.add(recipientId));
                    return nextReadIds;
                });

                if (convoUuid) {
                    this.localUnreadCountOverrides.update((overrides) => {
                        const nextOverrides = new Map(overrides);
                        nextOverrides.set(convoUuid, 0);
                        return nextOverrides;
                    });
                }
            })
            .catch((error) => S25Util.showError(error))
            .finally(() => {
                this.markingReadRecipientIds.update((markingIds) => {
                    const nextMarkingIds = new Set(markingIds);
                    pendingRecipientIds.forEach((recipientId) => nextMarkingIds.delete(recipientId));
                    return nextMarkingIds;
                });
            });
    }

    private isMobileLayout(): boolean {
        return window.matchMedia(`(max-width: ${COMPACT_LAYOUT_MAX_WIDTH_PX}px)`).matches;
    }
}
